import { useEffect, useMemo, useState } from "react";
import type { WidgetUnit } from "../../types";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../../lib/image";
import { WidgetLayer } from "../WidgetLayer";

// Room-detail pop-up for an accommodation unit — a photo lightbox (prev/next +
// thumbnails), the room description, an amenities list, and a "Book this room"
// CTA. Opened by clicking the card or its "View details" control. Photos come
// from service_units.gallery_image_urls (Cloudbeds roomTypePhotos) and
// amenities from service_units.amenities (Cloudbeds roomTypeFeatures).
export function RoomGalleryModal({
  unit,
  onClose,
  onBook,
  bookDisabled = false,
  bookLabel,
}: {
  unit: WidgetUnit;
  onClose: () => void;
  onBook: () => void;
  bookDisabled?: boolean;
  bookLabel?: string;
}) {
  const images = useMemo(() => {
    const g = (unit.gallery_image_urls ?? []).filter(Boolean);
    return g.length > 0 ? g : unit.image_url ? [unit.image_url] : [];
  }, [unit]);
  const amenities = useMemo(
    () => (unit.amenities ?? []).filter(Boolean),
    [unit],
  );
  const count = images.length;
  const [idx, setIdx] = useState(0);
  const go = (d: number) =>
    setIdx((i) => (count ? (i + d + count) % count : 0));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const hasAnything =
    count > 0 || amenities.length > 0 || Boolean(unit.description);

  return (
    <WidgetLayer>
      <div className="vt-gallery-backdrop" onClick={onClose}>
        <div
          className="vt-gallery-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${unit.name} details`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="vt-gallery-head">
            <div className="vt-gallery-title">{unit.name}</div>
            <button
              type="button"
              className="vt-gallery-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <div className="vt-gallery-body">
            {count > 0 && (
              <>
                <div className="vt-gallery-stage">
                  {count > 1 && (
                    <button
                      type="button"
                      className="vt-gallery-nav vt-gallery-prev"
                      aria-label="Previous photo"
                      onClick={() => go(-1)}
                    >
                      ‹
                    </button>
                  )}
                  <img
                    className="vt-gallery-main"
                    src={optimizedImageUrl(images[idx], { width: 1200, fit: "contain" })}
                    srcSet={optimizedSrcSet(images[idx], IMG_WIDTHS.GALLERY_MAIN, { fit: "contain" })}
                    sizes="100vw"
                    alt={`${unit.name} — photo ${idx + 1} of ${count}`}
                    decoding="async"
                  />
                  {count > 1 && (
                    <button
                      type="button"
                      className="vt-gallery-nav vt-gallery-next"
                      aria-label="Next photo"
                      onClick={() => go(1)}
                    >
                      ›
                    </button>
                  )}
                  {count > 1 && (
                    <div className="vt-gallery-counter">
                      {idx + 1} / {count}
                    </div>
                  )}
                </div>
                {count > 1 && (
                  <div className="vt-gallery-thumbs">
                    {images.map((src, i) => (
                      <button
                        key={`${src}-${i}`}
                        type="button"
                        className={`vt-gallery-thumb${i === idx ? " vt-gallery-thumb-active" : ""}`}
                        aria-label={`Photo ${i + 1}`}
                        onClick={() => setIdx(i)}
                      >
                        <img
                          src={optimizedImageUrl(src, { width: 200 })}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {unit.description && (
              <p className="vt-gallery-desc">{unit.description}</p>
            )}
            {amenities.length > 0 && (
              <div className="vt-gallery-amenities">
                <div className="vt-gallery-amenities-title">Amenities</div>
                <ul className="vt-gallery-amenities-list">
                  {amenities.map((a, i) => (
                    <li key={`${a}-${i}`} className="vt-gallery-amenity">
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!hasAnything && (
              <div className="vt-gallery-empty">No extra details yet.</div>
            )}
          </div>
          <div className="vt-gallery-foot">
            <button
              type="button"
              className="vt-gallery-book-cta"
              onClick={onBook} disabled={bookDisabled}
            >
              {bookLabel ?? "Book this room"}
            </button>
          </div>
        </div>
      </div>
    </WidgetLayer>
  );
}
